import { type PropsWithChildren, useEffect, useRef } from 'react';
import { OktaAuthProvider, useOktaAuth } from '@LaSalle-invest/okta-mfe-auth/react';
import type { OktaConfig, UseOktaAuthReturn } from '@LaSalle-invest/okta-mfe-auth/react';
import { isHostMode } from './isHostMode';
import oktaConfig from '../config/okta';
import { HostAuthContextSync, StandaloneAuthContextSync } from './authContextSync';

function StandaloneGate({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading, error, login } =
    useOktaAuth() as UseOktaAuthReturn<Record<string, unknown>>;

  const loginStarted = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    if (error) return;
    if (loginStarted.current) return;

    loginStarted.current = true;
    void login();
  }, [isAuthenticated, isLoading, error, login]);

  if (isLoading || (!isAuthenticated && !error)) {
    return <div>Authenticating...</div>;
  }

  if (error) {
    return (
      <div>
        <div>Authentication error</div>
        <pre>{error.message}</pre>
      </div>
    );
  }

  return <>{children}</>;
}

export default function AuthBoundary({ children }: PropsWithChildren) {
  // When AUTH_TOKEN_OVERRIDE is set in .env, bypass the entire Okta auth flow.
  // This lets you test locally with an externally-obtained token (e.g. from prod)
  // without needing a valid Okta session. The token itself is injected into
  // requests via getAccessToken() in okta.config.ts.
  if (import.meta.env.VITE_AUTH_TOKEN_OVERRIDE) {
    return <>{children}</>;
  }

  // Host-mode: host already authenticated; never redirect to Okta from this MFE.
  if (isHostMode()) {
    return (
      <>
        <HostAuthContextSync />
        {children}
      </>
    );
  }

  return (
    <OktaAuthProvider config={oktaConfig as OktaConfig}>
      <StandaloneAuthContextSync />
      <StandaloneGate>{children}</StandaloneGate>
    </OktaAuthProvider>
  );
}
